import React from 'react';
import PropTypes from 'prop-types';
import Icon, { iconsDictionary } from './Icon';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

export default {
  title: 'Components/Icons/Icon',
  component: Icon,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `Icon` component renders SVG icons dynamically based on the `name` prop. It simplifies the inclusion of scalable and reusable icons throughout the application by maintaining an organized dictionary of icon assets.\n\n',
      // When & how to use it?
      '\nUse the `Icon` component whenever you need to include scalable vector icons in your UI. Pass the appropriate `name` from the predefined `iconsDictionary` to render the desired icon dynamically.\n\nThis component is ideal for buttons, navigation elements, or anywhere an icon is required to enhance the UI.\n\n',
      // Current Usage
      `\nThe \`Icon\` component is widely used throughout the app in various components, including:
      \n- Buttons
      \n- Breadcrumbs
      \n- EmptyState
      \n- VisitsPage
      \n- Pagination
      \n- Cards and tables etc.
      \n\nIt ensures a consistent design language across the application.\n\n`,
      // Related Components
      `\n- **[Button](http://localhost:6006/?path=/docs/components-button--docs)**: Uses the \`Icon\` component for adding visual indicators.
      \n- **[Breadcrumbs](http://localhost:6006/?path=/docs/components-breadcrumbs--docs)**: Integrates \`Icon\` for navigation arrows etc.\n\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    name: {
      control: { type: 'text' },
      description: 'The key from `iconsDictionary` representing the icon to render.\n\nRefer to the dictionary for available icon names.',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: 'expand_left' },
        required: false,
      },
    },
    className: {
      control: { type: 'text' },
      description: 'Custom CSS classes for styling the icon.\n\n',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: '' },
        required: false,
      },
    },
  },
};

const IconTemplate = ({ name = 'expand_left', className = '' }) => (
  <div className="flex justify-center items-center">
    <Icon name={name} className={className} />
  </div>
);

IconTemplate.propTypes = {
  name: PropTypes.string,
  className: PropTypes.string,
};

const Template = IconTemplate.bind({});

export const DefaultIcon = Template.bind({});
DefaultIcon.args = {
  name: 'expand_left',
  className: 'h-10 w-10',
};
DefaultIcon.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant demonstrates the default behavior of the `Icon` component with the `expand_left` icon and customizable size using CSS classes.\n\n',
  // Where is it currently used on the product?
  '\nThe default variant is used across various components like navigation and actions in the app to ensure consistency in icon usage.\n\n',
);

export const AllIcons = Template.bind({});
AllIcons.args = {
  name: 'expand_left',
  className: 'h-10 w-10',
};
AllIcons.decorators = [
  () => (
    // added background color to demonstrate white icons
    (
      <div className="grid grid-cols-4 gap-6 bg-vamos-blue-gray">
        {Object.keys(iconsDictionary).map((iconKey) => (
          <div key={iconKey} className="flex flex-col items-center">
            <Icon name={iconKey} className="h-10 w-10" />
            <p className="text-xs text-gray-500">{iconKey}</p>
          </div>
        ))}
      </div>
    )
  ),
];
AllIcons.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant showcases all available icons from the `iconsDictionary`, making it easy for developers to find and use the appropriate icon.\n\n',
  // Where is it currently used on the product?
  '\nThe `AllIcons` variant is useful for development and design reference, helping teams select the correct icons for UI components.\n\n',
);
